import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaHeartbeat, FaQuoteLeft, FaStar, FaChartLine, FaWeight,
  FaTrophy, FaCalendarAlt, FaMapMarkerAlt, FaUserCircle,
  FaCheckCircle, FaArrowRight, FaPlay
} from 'react-icons/fa';
import images from '../assets/images';
import SEO, { PageSEO } from '../components/SEO';

const txCtx = require.context('../assets/Tranformations', false, /\.(jpeg|jpg|png|webp)$/i);
const TX_IMAGES = txCtx.keys().sort().map(k => txCtx(k));

const SuccessStories = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStory, setSelectedStory] = useState(null);

  const categories = [
    { id: 'all', label: 'All Stories' },
    { id: 'weight-loss', label: 'Weight Loss' },
    { id: 'diabetes', label: 'Diabetes Reversal' },
    { id: 'lifestyle', label: 'Lifestyle Change' },
    { id: 'confidence', label: 'Confidence & Wellness' }
  ];

  const successStories = [
    {
      id: 1,
      name: "Priya Sharma",
      age: 34,
      location: "Mumbai, India",
      occupation: "Marketing Manager",
      category: "weight-loss",
      weightLost: 23,
      duration: 8,
      startWeight: 92,
      currentWeight: 69,
      bmiStart: 34.2,
      bmiCurrent: 25.6,
      image: images.testimonials.person1,
      quote: "I tried everything—keto, intermittent fasting, gym memberships I never used. Nothing stuck until Fitnession. The medication controlled my cravings, and my coach helped me understand WHY I was overeating.",
      beforeAfter: {
        before: "I was exhausted all the time, avoiding social events, and my pre-diabetes was getting worse.",
        after: "I have energy to play with my kids, I reversed my pre-diabetes, and I finally feel confident in my own skin."
      },
      keyWins: [
        "Lost 23 kg in 8 months",
        "Reversed pre-diabetes (HbA1c from 6.2 to 5.4)",
        "Off blood pressure medication",
        "Completed first 5K run",
        "Gained confidence at work"
      ],
      favoriteFeature: "Weekly coaching calls kept me accountable even when I wanted to give up.",
      videoTestimonial: null // Would be video URL
    },
    {
      id: 2,
      name: "Rajesh Patel",
      age: 42,
      location: "Bangalore, India",
      occupation: "Software Engineer",
      category: "diabetes",
      weightLost: 28,
      duration: 10,
      startWeight: 105,
      currentWeight: 77,
      bmiStart: 36.8,
      bmiCurrent: 27.0,
      image: images.testimonials.person2,
      quote: "My doctor said I'd need insulin soon. That scared me. Fitnession gave me the medical support and tools to completely turn my health around. My endocrinologist is amazed.",
      beforeAfter: {
        before: "Type 2 diabetes, high cholesterol, sleep apnea. Taking 5 different medications daily.",
        after: "No longer diabetic, cholesterol normal, off CPAP machine. Only on minimal medication now."
      },
      keyWins: [
        "Lost 28 kg in 10 months",
        "Reversed Type 2 Diabetes completely",
        "Stopped using CPAP for sleep apnea",
        "Reduced medications from 5 to 1",
        "Energy levels through the roof"
      ],
      favoriteFeature: "The monthly doctor check-ins gave me confidence that I was doing this safely and correctly.",
      videoTestimonial: null
    },
    {
      id: 3,
      name: "Anjali Reddy",
      age: 29,
      location: "Hyderabad, India",
      occupation: "HR Professional",
      category: "confidence",
      weightLost: 18,
      duration: 6,
      startWeight: 78,
      currentWeight: 60,
      bmiStart: 31.2,
      bmiCurrent: 24.0,
      image: images.testimonials.person3,
      quote: "PCOS made it impossible to lose weight. I was frustrated and losing hope. Fitnession's holistic approach finally worked for me. It's not just about the medication—it's the complete lifestyle change.",
      beforeAfter: {
        before: "Struggling with PCOS, irregular cycles, severe mood swings, and couldn't lose weight despite trying.",
        after: "Regular cycles, stable mood, lost the weight, and planning my wedding with confidence!"
      },
      keyWins: [
        "Lost 18 kg in 6 months",
        "PCOS symptoms significantly improved",
        "Regular menstrual cycles restored",
        "Skin cleared up",
        "Found confidence to get engaged"
      ],
      favoriteFeature: "The personalized meal plans that worked with my PCOS. My coach understood my hormonal challenges.",
      videoTestimonial: null
    },
    {
      id: 4,
      name: "Vikram Singh",
      age: 38,
      location: "Delhi, India",
      occupation: "Business Owner",
      category: "lifestyle",
      weightLost: 32,
      duration: 12,
      startWeight: 118,
      currentWeight: 86,
      bmiStart: 38.5,
      bmiCurrent: 28.0,
      image: images.testimonials.person4,
      quote: "Running a business meant no time for health. I needed something that worked with my crazy schedule. Fitnession's flexibility and the fact that everything was delivered to me made all the difference.",
      beforeAfter: {
        before: "Obese, high blood pressure, family history of heart disease. Stressed and worried about my future.",
        after: "Lost over 30 kg, blood pressure normal, and I'm teaching my kids healthy habits. Best investment I ever made."
      },
      keyWins: [
        "Lost 32 kg in 12 months",
        "Blood pressure normalized (off medication)",
        "Improved cardiovascular health markers",
        "Better sleep quality",
        "More present with family"
      ],
      favoriteFeature: "Home delivery of medication and the ability to do everything from my phone. No gym required.",
      videoTestimonial: null
    },
    {
      id: 5,
      name: "Meera Iyer",
      age: 45,
      location: "Chennai, India",
      occupation: "Homemaker",
      category: "weight-loss",
      weightLost: 20,
      duration: 9,
      startWeight: 85,
      currentWeight: 65,
      bmiStart: 33.0,
      bmiCurrent: 25.2,
      image: images.testimonials.person5,
      quote: "After two kids and thyroid issues, I thought I'd never get my body back. Fitnession understood my hormonal challenges and worked with my limitations, not against them.",
      beforeAfter: {
        before: "Post-pregnancy weight that wouldn't budge, thyroid issues, low energy, avoiding mirrors and photos.",
        after: "Back to my pre-pregnancy weight, thyroid controlled, full of energy, and loving life again!"
      },
      keyWins: [
        "Lost 20 kg in 9 months",
        "Thyroid levels stabilized",
        "Fit back into pre-pregnancy clothes",
        "Energy to keep up with kids",
        "Confidence restored"
      ],
      favoriteFeature: "My coach helped me navigate emotional eating. That was the missing piece all along.",
      videoTestimonial: null
    },
    {
      id: 6,
      name: "Arjun Malhotra",
      age: 31,
      location: "Pune, India",
      occupation: "Financial Analyst",
      category: "lifestyle",
      weightLost: 25,
      duration: 7,
      startWeight: 95,
      currentWeight: 70,
      bmiStart: 32.8,
      bmiCurrent: 24.2,
      image: images.testimonials.person6,
      quote: "I'm a numbers guy, so I loved seeing the data. Every week, the progress was measurable. The combination of medication and coaching was scientifically sound and it worked.",
      beforeAfter: {
        before: "Desk job weight gain, pre-diabetic, fatty liver, couldn't climb stairs without being out of breath.",
        after: "Athletic build, all health markers normal, running half-marathons. Complete transformation."
      },
      keyWins: [
        "Lost 25 kg in 7 months",
        "Fatty liver resolved",
        "Pre-diabetes reversed",
        "Running half-marathons",
        "Built sustainable habits"
      ],
      favoriteFeature: "The app's progress tracking. I could see every metric improving week by week. Data-driven results.",
      videoTestimonial: null
    }
  ];

  const stats = [
    { value: "1000+", label: "Lives Transformed", icon: FaHeartbeat },
    { value: "18%", label: "Average Weight Loss", icon: FaWeight },
    { value: "94%", label: "Member Satisfaction", icon: FaStar },
    { value: "85%", label: "Keep Weight Off", icon: FaTrophy }
  ];

  const filteredStories = selectedCategory === 'all' 
    ? successStories 
    : successStories.filter(story => story.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <SEO {...PageSEO.successStories} />
      {/* Hero Section - Compact */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
              <FaTrophy className="text-success-400 text-sm" />
              <span className="text-xs font-medium">Real People, Real Results</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
              Success Stories
            </h1>
            <p className="text-base text-primary-100 mb-6 leading-relaxed max-w-2xl mx-auto">
              Meet the thousands who've transformed their health with Fitnession.
            </p>
          </div>

          {/* Stats - Compact */}
          <div className="grid grid-cols-4 gap-4 mt-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                <stat.icon className="text-2xl text-success-400 mx-auto mb-1" />
                <div className="text-xl font-bold">{stat.value}</div>
                <div className="text-xs text-primary-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L60 5C120 10 240 20 360 23.3C480 27 600 23 720 21.7C840 20 960 20 1080 23.3C1200 27 1320 33 1380 36.7L1440 40V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0V0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Category Filter - Compact */}
      <section className="py-4 border-b border-gray-200 sticky top-0 bg-white z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Transformation Photos Gallery */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-2 space-y-2">
            {TX_IMAGES.map((img, i) => (
              <div key={i} className="break-inside-avoid overflow-hidden rounded-xl">
                <img
                  src={img}
                  alt=""
                  className="w-full object-cover rounded-xl hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStories.map((story) => (
              <StoryCard
                key={story.id}
                story={story}
                onReadMore={() => setSelectedStory(story)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Your Success Story Starts Here
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join the thousands who've transformed their health. Take the first step today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link
              to="/pricing"
              className="w-full sm:w-auto bg-white text-primary-700 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-xl"
            >
              View Programs
            </Link>
            <Link
              to="/our-program"
              className="w-full sm:w-auto bg-primary-500 border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-primary-400 transition-all"
            >
              Learn About Our Program
            </Link>
          </div>
        </div>
      </section>

      {/* Story Detail Modal */}
      {selectedStory && (
        <StoryDetailModal 
          story={selectedStory} 
          onClose={() => setSelectedStory(null)}
        />
      )}
    </div>
  );
};

// Story Card Component - Compact Version
const StoryCard = ({ story, onReadMore }) => {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all border border-gray-100 flex flex-col">
      {/* Header - Compact */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4 text-white">
        <div className="flex items-center space-x-3">
          {/* Profile photo hidden — will be added later */}
          {/* <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/30">
            {story.image ? (
              <img
                src={story.image}
                alt={story.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-white/20 flex items-center justify-center">
                <FaUserCircle className="text-2xl text-white" />
              </div>
            )}
          </div> */}
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">{story.name}</h3>
            <p className="text-primary-100 text-xs truncate">
              {story.age} • {story.occupation}
            </p>
          </div>
        </div>

        {/* Weight Loss Badge - Compact */}
        <div className="mt-3 inline-flex items-center bg-success-500 px-3 py-1.5 rounded-full text-sm">
          <FaWeight className="mr-1.5 text-xs" />
          <span className="font-semibold">-{story.weightLost} kg in {story.duration} mo</span>
        </div>
      </div>

      {/* Content - Compact */}
      <div className="p-4 flex-1 flex flex-col">
        {/* Quote - Truncated */}
        <div className="mb-4">
          <FaQuoteLeft className="text-primary-200 text-xl mb-2" />
          <p className="text-gray-600 text-sm italic leading-relaxed line-clamp-3">
            "{story.quote}"
          </p>
        </div>

        {/* Key Stats - Compact */}
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-center">
            <p className="text-xs text-gray-500">Before</p>
            <p className="text-lg font-bold text-gray-900">{story.startWeight} kg</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">After</p>
            <p className="text-lg font-bold text-success-600">{story.currentWeight} kg</p>
          </div>
        </div>

        {/* Key Wins Preview - Show 2 only */}
        <div className="mb-4 flex-1">
          <ul className="space-y-1.5">
            {story.keyWins.slice(0, 2).map((win, index) => (
              <li key={index} className="flex items-start text-gray-600 text-sm">
                <FaCheckCircle className="text-success-500 mt-0.5 mr-2 flex-shrink-0 text-xs" />
                <span className="line-clamp-1">{win}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Read More Button - Compact */}
        <button
          onClick={onReadMore}
          className="w-full bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center text-sm"
        >
          Read Full Story
          <FaArrowRight className="ml-2 text-xs" />
        </button>
      </div>
    </div>
  );
};

// Story Detail Modal Component
const StoryDetailModal = ({ story, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              {/* Profile photo hidden — will be added later */}
              {/* <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-white/30">
                {story.image ? (
                  <img
                    src={story.image}
                    alt={story.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-white/20 flex items-center justify-center">
                    <FaUserCircle className="text-5xl text-white" />
                  </div>
                )}
              </div> */}
              <div>
                <h2 className="text-3xl font-bold">{story.name}'s Journey</h2>
                <p className="text-primary-100">
                  {story.age} • {story.occupation} • {story.location}
                </p>
                <div className="flex items-center mt-2 space-x-2">
                  <span className="bg-success-500 px-3 py-1 rounded-full text-sm font-semibold">
                    -{story.weightLost} kg
                  </span>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    {story.duration} months
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* The Quote */}
          <div className="mb-8 p-6 bg-primary-50 border-l-4 border-primary-600 rounded-r-lg">
            <FaQuoteLeft className="text-primary-300 text-4xl mb-4" />
            <p className="text-xl text-gray-800 italic leading-relaxed">
              "{story.quote}"
            </p>
          </div>

          {/* Before & After */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FaChartLine className="text-primary-600 mr-3" />
              The Transformation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-red-50 border-2 border-red-200 rounded-xl">
                <h4 className="font-bold text-red-900 mb-3">Before</h4>
                <p className="text-gray-700">{story.beforeAfter.before}</p>
              </div>
              <div className="p-6 bg-success-50 border-2 border-success-200 rounded-xl">
                <h4 className="font-bold text-success-900 mb-3">After</h4>
                <p className="text-gray-700">{story.beforeAfter.after}</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-xl text-center">
              <p className="text-sm text-gray-600 mb-1">Start Weight</p>
              <p className="text-2xl font-bold text-gray-900">{story.startWeight} kg</p>
            </div>
            <div className="bg-success-50 p-4 rounded-xl text-center">
              <p className="text-sm text-gray-600 mb-1">Current Weight</p>
              <p className="text-2xl font-bold text-success-600">{story.currentWeight} kg</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl text-center">
              <p className="text-sm text-gray-600 mb-1">BMI Change</p>
              <p className="text-2xl font-bold text-gray-900">
                {story.bmiStart} → {story.bmiCurrent}
              </p>
            </div>
            <div className="bg-primary-50 p-4 rounded-xl text-center">
              <p className="text-sm text-gray-600 mb-1">Duration</p>
              <p className="text-2xl font-bold text-primary-600">{story.duration} mo</p>
            </div>
          </div>

          {/* All Key Wins */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FaTrophy className="text-success-600 mr-3" />
              All Achievements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {story.keyWins.map((win, index) => (
                <div key={index} className="flex items-start p-3 bg-success-50 rounded-lg">
                  <FaCheckCircle className="text-success-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{win}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Favorite Feature */}
          <div className="mb-8 p-6 bg-accent-50 border-l-4 border-accent-600 rounded-r-lg">
            <h4 className="font-bold text-accent-900 mb-2">Favorite Feature:</h4>
            <p className="text-gray-700 italic">"{story.favoriteFeature}"</p>
          </div>

          {/* CTA */}
          <div className="text-center pt-6 border-t">
            <p className="text-gray-600 mb-4">Ready to write your own success story?</p>
            <Link
              to="/pricing"
              onClick={onClose}
              className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-primary-700 transition-all shadow-lg"
            >
              Start Your Transformation
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessStories;